import dayjs from 'dayjs';
import { StaticOptionEntity } from '@/swagger';

const ACCEPTABLE_CONFERENCE_CITY_TAIWAN = [
  'taipei',
  'taichung',
  'kaohsiung',
  'hsinchu',
  'tainan',
  'taoyuan',
  'yunlin',
] as const;

const ACCEPTABLE_CONFERENCE_CITY_ABROAD = ['abroad', 'newyork', 'phoenix', 'texas'] as const;
const ACCEPTABLE_CONFERENCE_CITY = [...ACCEPTABLE_CONFERENCE_CITY_TAIWAN, ...ACCEPTABLE_CONFERENCE_CITY_ABROAD];

const ACCEPTABLE_CONFERENCE_CITY_LABEL: Record<(typeof ACCEPTABLE_CONFERENCE_CITY)[number], string> = {
  taipei: '台北',
  taichung: '台中',
  kaohsiung: '高雄',
  hsinchu: '新竹',
  tainan: '台南',
  taoyuan: '桃園',
  yunlin: '雲林',

  // aboard
  abroad: '海外',
  newyork: '紐約',
  phoenix: '鳳凰城',
  texas: '德州',
};
const ACCEPTABLE_CONFERENCE_FORMAT = new RegExp(
  `^(${ACCEPTABLE_CONFERENCE_CITY.join('|')})_(\\d{8})_(\\d{4})(?:_(\\d{4}))?$`
);
export const stringToOption = (value: string): Option => ({
  value: value,
  label: value,
});

const formatConferenceDateTime = (date: string, time: string) => {
  const year = date.slice(0, 4);
  const month = date.slice(4, 6).replace(/^0+/g, '') || '0';
  const day = date.slice(6, 8).replace(/^0+/g, '') || '0';
  const hour = time.slice(0, 2).replace(/^0+/g, '') || '0';
  const minute = time.slice(2, 4);

  return dayjs()
    .set('year', Number(year))
    .set('month', Number(month) - 1)
    .set('date', Number(day))
    .set('hour', Number(hour))
    .set('minute', Number(minute))
    .set('second', 0)
    .set('millisecond', 0);
};

interface SerializedConferenceOption extends StaticOptionEntity {
  city: string;
  date: string;
  time: string;
  isOver: boolean;
  isAcceptable: boolean;
}

const serializeConferenceValue = (value: string) => {
  const [_, city = '', theDate = '', theTime = '', theEndTime = ''] = ACCEPTABLE_CONFERENCE_FORMAT.exec(value) ?? [];
  const serializedDate = formatConferenceDateTime(theDate, theTime);

  const formattedDate = serializedDate.format('YYYY 年 MM 月 DD 日 dddd');
  const endDate = theEndTime ? formatConferenceDateTime(theDate, theEndTime) : serializedDate.add(60, 'minutes');

  const startTime = serializedDate.format('HH:mm');
  const endTime = endDate.format('HH:mm');

  return {
    city: ACCEPTABLE_CONFERENCE_CITY_LABEL[city as (typeof ACCEPTABLE_CONFERENCE_CITY)[number]],
    date: formattedDate,
    time: `${startTime}–${endTime}`,
    /**
     * If the start date is after or equal to today, it means the conference is over.
     * Which means if the start date is 2025/4/10 15:00, and today is 2025/4/10 00:00,
     * then the conference is over.
     */
    isOver: dayjs().isSame(serializedDate, 'day') || dayjs().isAfter(serializedDate),
    isAcceptable: ACCEPTABLE_CONFERENCE_FORMAT.test(value) && serializedDate.isValid(),
  };
};
export const conferenceSerializer = (options: Array<StaticOptionEntity>): Array<SerializedConferenceOption> => {
  return options
    .filter(({ active }) => active)
    .map((option) => ({
      ...serializeConferenceValue(option.value),
      ...option,
    }))
    .filter(({ isOver, isAcceptable }) => !isOver && isAcceptable);
};
